/**
 * Extension fields editor — /settings/extensions/$moduleKey/$entityKey/fields
 *
 * P2.3: lets a tenant admin add/edit/delete custom fields attached to
 * a built-in (or custom) entity. The entity itself is not modified —
 * fields live in the separate `extensions.entity_fields` table and
 * their values in `extensions.entity_field_values`, joined into
 * built-in responses by the extension helper library.
 *
 * The page reuses `FieldsEditor` from the custom-modules package,
 * so field type pickers, required/unique toggles, select options,
 * and type-specific config are all identical to the custom-entity
 * editor.
 * This is the payoff of sharing the FieldDefinition shape — one
 * editor covers both custom and built-in extensions.
 *
 * Flow:
 *   - Loads the list of existing extension fields on mount
 *   - `FieldsEditor` renders them as a list
 *   - Save button fires per-field create/update/delete against the
 *     oRPC `modules.extensions.entityFields.*` endpoints, diffing
 *     the current UI state against the last-saved snapshot
 *   - `indexed` flag per field opts into the expression index (fast
 *     filtering for that field)
 */

import type { FieldDefinition } from '@cloud/custom-modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { FieldsEditor } from '@/components/custom/fields-editor'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/extensions/$moduleKey/$entityKey/fields')({
  component: ExtensionFieldsPage,
})

/**
 * Hydrated field as the UI holds it — the backend's JSONB
 * `definition` plus the top-level `indexed` flag. Kept as one shape
 * so the editor doesn't need to know about the storage layout.
 */
interface ExtensionFieldDraft extends FieldDefinition {
  /**
   * Whether an expression index exists for this field. True = fast
   * filtering; false = no index. Toggled in the field editor.
   */
  _indexed?: boolean
  /**
   * True if this field already exists on the server. Used at save
   * time to decide create vs update.
   */
  _persisted?: boolean
}

function ExtensionFieldsPage() {
  const { moduleKey, entityKey } = Route.useParams()
  const queryClient = useQueryClient()

  const { data: serverFields, isLoading } = useQuery(
    orpc.extensions.entityFields.list.queryOptions({
      input: { moduleKey, entityKey },
    }),
  )

  // UI state — the editable draft list. Initialized from the server
  // response and then owned by the user until they save.
  const [draft, setDraft] = useState<ExtensionFieldDraft[]>([])

  // Hydrate the draft from the server on first load. We key the effect
  // off the data reference so a refetch after save also reseeds the
  // draft (picking up server-generated ids and clean `_persisted`).
  useEffect(() => {
    if (!serverFields) return
    setDraft(
      serverFields.map((f) => ({
        ...(f.definition as unknown as FieldDefinition),
        _indexed: f.indexed,
        _persisted: true,
      })),
    )
  }, [serverFields])

  const createMutation = useMutation(
    orpc.extensions.entityFields.create.mutationOptions({
      onError: (err) => toast.error(err.message),
    }),
  )
  const updateMutation = useMutation(
    orpc.extensions.entityFields.update.mutationOptions({
      onError: (err) => toast.error(err.message),
    }),
  )
  const deleteMutation = useMutation(
    orpc.extensions.entityFields.delete.mutationOptions({
      onError: (err) => toast.error(err.message),
    }),
  )

  const isSaving =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  /**
   * Diff the draft against the server snapshot and apply the
   * resulting creates / updates / deletes sequentially. Sequential
   * is deliberate: if one write fails, subsequent ones halt and the
   * user sees a partial state they can retry from.
   */
  const handleSave = async () => {
    if (!serverFields) return

    const serverByKey = new Map(serverFields.map((f) => [f.fieldKey, f]))
    const draftByKey = new Map(draft.map((f) => [f.key, f]))

    // Deletes: server has a field that's no longer in draft
    const toDelete = serverFields.filter((f) => !draftByKey.has(f.fieldKey))
    // Creates: draft has a field that doesn't exist on the server
    const toCreate = draft.filter((f) => !f._persisted)
    // Updates: same key on both sides, but the definition or indexed
    // flag differs. Simple deep-ish equality on the JSON blob is
    // enough — the UI roundtrips unchanged fields identically.
    const toUpdate = draft.filter((f) => {
      if (!f._persisted) return false
      const existing = serverByKey.get(f.key)
      if (!existing) return false
      const { _indexed, _persisted, ...cleanDef } = f
      void _persisted
      const sameDef =
        JSON.stringify(cleanDef) === JSON.stringify(existing.definition)
      const sameIndexed = (f._indexed ?? false) === existing.indexed
      return !(sameDef && sameIndexed)
    })

    try {
      for (const f of toDelete) {
        await deleteMutation.mutateAsync({
          moduleKey,
          entityKey,
          fieldKey: f.fieldKey,
        })
      }
      for (const f of toCreate) {
        const { _indexed, _persisted, ...cleanDef } = f
        void _persisted
        await createMutation.mutateAsync({
          moduleKey,
          entityKey,
          field: cleanDef as FieldDefinition,
          indexed: _indexed ?? false,
        })
      }
      for (const f of toUpdate) {
        const { _indexed, _persisted, ...cleanDef } = f
        void _persisted
        await updateMutation.mutateAsync({
          moduleKey,
          entityKey,
          fieldKey: f.key,
          field: cleanDef as FieldDefinition,
          indexed: _indexed ?? false,
        })
      }

      toast.success('Extension fields saved')
      void queryClient.invalidateQueries({
        queryKey: orpc.extensions.entityFields.list.queryKey({
          input: { moduleKey, entityKey },
        }),
      })
    } catch {
      // Individual errors are already surfaced via the mutation
      // onError toasts. This catch just stops the sequence.
    }
  }

  if (isLoading) {
    return <Skeleton className="h-96 w-full" />
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
      <Link
        to="/settings"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to settings
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>
            Custom fields on{' '}
            <code className="font-mono text-base">
              {moduleKey}.{entityKey}
            </code>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Add your own fields to this built-in entity. Fields are stored separately from
            the core record and joined in at read time — the underlying table is never
            modified.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <FieldsEditor fields={draft} onChange={(next) => setDraft(next)} />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" onClick={handleSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
